import React, { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

interface ChainHop {
    lwin: number;
    status: string | null;
    displayName: string | null;
}

interface LwinChainProps {
    lwin: number | null | undefined;
}

// Small in-module cache so the same LWIN isn't re-fetched across rows/renders.
const cache = new Map<number, ChainHop[]>();

/**
 * Renders a LWIN's resolution chain (Combined → … → Live) inline. Stays silent for a LWIN that is
 * already canonical (single, non-Combined hop) — only surfaces when there's an actual redirect.
 */
const LwinChain: React.FC<LwinChainProps> = ({ lwin }) => {
    const [hops, setHops] = useState<ChainHop[] | null>(lwin != null ? cache.get(lwin) ?? null : null);

    useEffect(() => {
        if (lwin == null) return;
        if (cache.has(lwin)) { setHops(cache.get(lwin)!); return; }
        let cancelled = false;
        fetch(`${API_BASE_URL}/api/lwin-references/${lwin}/chain`)
            .then(r => (r.ok ? r.json() : []))
            .then((data: ChainHop[]) => {
                cache.set(lwin, data);
                if (!cancelled) setHops(data);
            })
            .catch(() => { if (!cancelled) setHops([]); });
        return () => { cancelled = true; };
    }, [lwin]);

    // Nothing to show: no redirect (a single, already-canonical hop).
    if (!hops || hops.length <= 1) return null;

    const styles: { [k: string]: React.CSSProperties } = {
        wrap: { display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px', fontSize: '11px', fontFamily: 'monospace', color: '#6b7280' },
        alias: { color: '#9ca3af', textDecoration: 'line-through' },
        live: { color: '#166534', fontWeight: 600 },
        liveTag: { fontFamily: 'inherit', fontSize: '10px', backgroundColor: '#dcfce7', color: '#166534', borderRadius: '5px', padding: '0 5px', marginLeft: '3px' },
        name: { fontFamily: 'inherit', color: '#9ca3af' },
    };

    return (
        <span style={styles.wrap} title="LWIN resolution chain (Liv-ex Combined → reference)">
            {hops.map((h, i) => {
                const isLast = i === hops.length - 1;
                return (
                    <React.Fragment key={h.lwin}>
                        <span style={isLast ? styles.live : styles.alias}>{h.lwin}</span>
                        {isLast && h.status && h.status.toLowerCase() !== 'combined' && (
                            <span style={styles.liveTag}>{h.status}</span>
                        )}
                        {isLast && h.displayName && <span style={styles.name}>· {h.displayName}</span>}
                        {!isLast && <ArrowRight size={11} color="#9ca3af" />}
                    </React.Fragment>
                );
            })}
        </span>
    );
};

export default LwinChain;
